import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { useFleetApi, type UnifiedTrace, type TraceTimelineRow } from "./api";

function fmtTs(iso: string): string {
  const d = new Date(iso);
  const hms = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  const ms = String(d.getMilliseconds()).padStart(3, "0");
  return `${hms}.${ms}`;
}

function rowTone(row_type: string): "ok" | "warn" | "crit" | "muted" {
  if (row_type === "escalation") return "warn";
  if (row_type === "action") return "ok";
  return "muted";
}

function describeRow(row: TraceTimelineRow): { title: string; body: React.ReactNode } {
  if (row.row_type === "audit") {
    const r = row as TraceTimelineRow & {
      agent: string; kind: string; tool: string | null;
      args: Record<string, unknown> | null;
      result: Record<string, unknown> | null;
      latency_ms: number | null;
    };
    const title = [r.agent, r.kind, r.tool].filter(Boolean).join(" · ")
      + (r.latency_ms != null ? ` · ${r.latency_ms}ms` : "");
    return {
      title,
      body: (
        <>
          {r.args && <pre className="json">{JSON.stringify(r.args, null, 2)}</pre>}
          {r.result && <pre className="json">{JSON.stringify(r.result, null, 2)}</pre>}
        </>
      ),
    };
  }
  if (row.row_type === "action") {
    const r = row as TraceTimelineRow & {
      agent: string; kind: string; subject: string | null;
      payload: Record<string, unknown> | null; reversible: boolean;
      reverted_at: string | null; reverted_reason: string | null;
    };
    return {
      title: `${r.agent} · ${r.kind}${r.subject ? ` · ${r.subject}` : ""}${r.reverted_at ? " · REVERTED" : ""}`,
      body: (
        <>
          {r.payload && <pre className="json">{JSON.stringify(r.payload, null, 2)}</pre>}
          {r.reverted_reason && (
            <p style={{ color: "var(--crit)", fontSize: "12px" }}>Reverted: {r.reverted_reason}</p>
          )}
        </>
      ),
    };
  }
  // escalation
  const r = row as TraceTimelineRow & {
    agent: string; situation_class: string; decision_class: string;
    reasoning: string; proposed_payload: Record<string, unknown> | null;
    status: string; resolution: string | null;
  };
  return {
    title: `${r.agent} · escalation · ${r.situation_class} → ${r.decision_class} · ${r.status}`,
    body: (
      <>
        <p style={{ whiteSpace: "pre-wrap", fontSize: "12.5px" }}>{r.reasoning}</p>
        {r.proposed_payload && <pre className="json">{JSON.stringify(r.proposed_payload, null, 2)}</pre>}
        {r.resolution && (
          <p style={{ color: "var(--text-2)", fontSize: "12px" }}>Resolution: {r.resolution}</p>
        )}
      </>
    ),
  };
}

export default function TraceViewer() {
  const { token, logout } = useAuth();
  const { fetchUnifiedTrace } = useFleetApi(token, logout);
  const [params, setParams] = useSearchParams();
  const initial = params.get("id") ?? "";

  const [traceId, setTraceId] = useState(initial);
  const [inputVal, setInputVal] = useState(initial);
  const [data, setData] = useState<UnifiedTrace | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const load = useCallback(
    (id: string) => {
      if (!id.trim()) return;
      setLoading(true);
      setErr(null);
      fetchUnifiedTrace(id)
        .then(d => { setData(d); setLoading(false); })
        .catch(e => { setErr(String(e)); setLoading(false); });
    },
    [fetchUnifiedTrace],
  );

  useEffect(() => {
    if (traceId) load(traceId);
  }, [traceId, load]);

  const submit = (val: string) => {
    const v = val.trim();
    setTraceId(v);
    const next = new URLSearchParams(params);
    if (v) next.set("id", v); else next.delete("id");
    setParams(next, { replace: true });
  };

  const wallMs =
    data && data.timeline.length > 1
      ? new Date(data.timeline[data.timeline.length - 1].created_at as string).getTime() -
        new Date(data.timeline[0].created_at as string).getTime()
      : null;

  return (
    <section>
      <span className="meta">
        {data && data.timeline.length > 0
          ? `${data.counts.audit} audit · ${data.counts.action} action · ${data.counts.escalation} escalation`
            + (wallMs != null ? ` · ${(wallMs / 1000).toFixed(1)}s wall` : "")
          : "paste a trace_id to inspect"}
      </span>
      <p className="page-lede">
        Every row tied to a trace across <code>fleet.audit</code>,
        <code> fleet.action</code> and <code>fleet.escalation</code>, in order. Click any row to expand.
      </p>

      <div className="chips" style={{ marginBottom: "16px" }}>
        <input
          className="search"
          placeholder="trace_id (e.g. tr-39812)"
          value={inputVal}
          onChange={e => setInputVal(e.target.value)}
          onKeyDown={e => e.key === "Enter" && submit(inputVal)}
          style={{ width: "360px" }}
        />
        <button
          className="opt"
          style={{ padding: "5px 12px", fontSize: "12.5px" }}
          onClick={() => submit(inputVal)}
          disabled={loading}
        >
          Load
        </button>
      </div>

      {err && <p style={{ color: "var(--crit)", fontSize: "12.5px" }}>{err}</p>}
      {loading && <p className="muted" style={{ fontSize: "12.5px" }}>Loading…</p>}

      {data && data.timeline.length === 0 && !loading && (
        <p className="muted">No rows found for this trace_id.</p>
      )}

      {data && data.timeline.length > 0 && (
        <div className="card">
          <div className="trace">
            {data.timeline.map((row, i) => {
              const id = `${row.row_type}-${row.id ?? i}`;
              const isOpen = expanded.has(id);
              const { title, body } = describeRow(row);
              return (
                <div key={id} className="tn" style={i === 0 ? { } : { marginTop: "4px" }}>
                  <div
                    className="tn-row"
                    style={{ cursor: "pointer" }}
                    onClick={() =>
                      setExpanded(prev => {
                        const next = new Set(prev);
                        if (next.has(id)) next.delete(id); else next.add(id);
                        return next;
                      })
                    }
                  >
                    <span className="ts">{fmtTs(row.created_at as string)}</span>
                    <span className="desc">
                      <span className={`pill ${rowTone(row.row_type)}`} style={{ marginRight: "6px" }}>
                        {row.row_type}
                      </span>
                      {title}
                    </span>
                  </div>
                  {isOpen && <div style={{ paddingLeft: "16px" }}>{body}</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}
